import * as z from "zod";
import { userFacingError } from "../authorization";
import { copyAllCommitFiles, findConflictedFiles } from "../commitFiles";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { mergeMainIfBehind } from "../mergeCore";
import { actorColumns, creatorColumns } from "../rbac/roles";
import { assertNotSelfApproval } from "./assertNotSelfApproval";
import { mutator } from "./mutator";
import { assertNotGitSynced } from "./syncGit";
import { generateId } from "../id";
import { clientGeneratableIdSchema } from "../id";
import type { Context } from "../authorization";
import type { Transaction } from "@rocicorp/zero";

export const pushBranchToLiveSchema = z.object({
  sourceBranchId: z.string(),
  newCommitId: clientGeneratableIdSchema,
  newChatId: clientGeneratableIdSchema.optional(),
  message: z.string(),
});

export const pushBranchToLive = mutator({
  schema: pushBranchToLiveSchema,
  resolve: {
    sourceBranch: { kind: "branch", from: "sourceBranchId" },
  },
  permissions: [
    {
      permission: "space:push_live",
      scopeId: ({ args }) => args.sourceBranch.workflow.spaceId,
    },
  ],
  fn: async (opts) => {
    await assertNotGitSynced(opts.tx, opts.args.sourceBranch.workflow.spaceId);
    const headCommitId = await mergeMainIfBehind(opts.tx, {
      branch: opts.args.sourceBranch,
      creator: creatorColumns(opts.ctx),
      tenantId: opts.ctx.tenantId,
    });
    await pushBranchToLiveInTransaction({
      ...opts,
      args: {
        ...opts.args,
        sourceBranch: { ...opts.args.sourceBranch, headCommitId },
      },
    });
  },
});

function listFiles(paths: string[]): string {
  if (paths.length <= 3) {
    if (paths.length === 1) return paths[0]!;
    return `${paths.slice(0, -1).join(", ")} and ${paths.at(-1)}`;
  }
  return `${paths.slice(0, 3).join(", ")} and ${paths.length - 3} more files`;
}

export async function pushBranchToLiveInTransaction({
  tx,
  ctx,
  args: { newCommitId, newChatId = generateId(), message, sourceBranch },
  deleteSourceBranch = true,
}: {
  tx: Transaction<Schema>;
  ctx: Context;
  args: {
    newCommitId: string;
    newChatId?: string;
    message: string;
    sourceBranch: {
      id: string;
      name: string;
      workflowId: string;
      headCommitId: string | null;
    };
  };
  deleteSourceBranch?: boolean;
}) {
  const creator = creatorColumns(ctx);
  const actor = actorColumns(ctx);
  const now = Date.now();
  // user_actions cannot attribute service accounts, so their marker is a system message.
  const markerEntryType = actor.userId ? "UserAction" : "SystemMessage";

  if (!sourceBranch.headCommitId) {
    throw userFacingError("Branch has no head commit");
  }

  const conflictedPaths = await findConflictedFiles({
    tx,
    commitId: sourceBranch.headCommitId,
  });
  if (conflictedPaths.length > 0) {
    throw userFacingError(
      `Conflicts in ${listFiles(conflictedPaths)} need to be resolved before pushing live.`
    );
  }

  // Both the optimistic and the authoritative server path run this; the
  // server result is what wins.
  await assertNotSelfApproval(
    tx,
    ctx.userId,
    sourceBranch.id,
    sourceBranch.workflowId
  );

  const [mainBranch] = await tx.run(
    zql.workflowBranches
      .where("workflowId", sourceBranch.workflowId)
      .where("name", "main")
  );

  const mainBranchId = mainBranch ? mainBranch.id : generateId();

  if (mainBranch) {
    await tx.mutate.workflowCommits.insert({
      id: newCommitId,
      branchId: mainBranchId,
      parentCommitId: mainBranch.headCommitId,
      chatId: newChatId,
      message,
      createdAt: now,
      ...creator,
      tenantId: ctx.tenantId,
    });
  } else {
    await tx.mutate.workflowBranches.insert({
      id: mainBranchId,
      workflowId: sourceBranch.workflowId,
      name: "main",
      headCommitId: newCommitId,
      createdAt: now,
      lastChatEntryAt: now,
      tenantId: ctx.tenantId,
    });

    await tx.mutate.workflowCommits.insert({
      id: newCommitId,
      branchId: mainBranchId,
      parentCommitId: null,
      chatId: newChatId,
      message,
      createdAt: now,
      ...creator,
      tenantId: ctx.tenantId,
    });
  }

  await copyAllCommitFiles(
    { tx, commitId: sourceBranch.headCommitId, tenantId: ctx.tenantId },
    newCommitId
  );

  const allEntries = (
    await tx.run(
      zql.workflowCommits
        .where("branchId", sourceBranch.id)
        .related("chats", (c) =>
          c.related("entries", (e) =>
            e
              .related("userMessage")
              .related("assistantMessage")
              .related("toolCall")
              .related("assistantReasoning")
              .related("systemMessage")
              .related("userAction")
          )
        )
    )
  )
    .flatMap((commit) => commit.chats)
    .flatMap((chat) => chat.entries)
    .sort((a, b) => a.createdAt - b.createdAt);

  await tx.mutate.chats.insert({
    id: newChatId,
    commitId: newCommitId,
    createdAt: now,
    lastChatEntryType: markerEntryType,
    tenantId: ctx.tenantId,
  });

  // TOOL_CALL_OUTPUT_V1_BACKCOMPAT start
  const legacyToolCallCopies: Array<{
    source: { entryId: string; chatId: string };
    destination: { entryId: string; chatId: string };
  }> = [];
  // TOOL_CALL_OUTPUT_V1_BACKCOMPAT end
  let previousNewId: string | null = null;
  for (const entry of allEntries) {
    const newId = generateId();
    await tx.mutate.chatEntries.insert({
      id: newId,
      chatId: newChatId,
      entryType: entry.entryType,
      createdAt: entry.createdAt,
      previousEntryId: previousNewId,
      tenantId: ctx.tenantId,
    });
    previousNewId = newId;
    if (entry.userMessage) {
      await tx.mutate.userMessages.insert({
        entryId: newId,
        userId: entry.userMessage.userId,
        serviceAccountId: entry.userMessage.serviceAccountId,
        text: entry.userMessage.text,
        tenantId: ctx.tenantId,
      });
    }
    if (entry.assistantMessage) {
      await tx.mutate.assistantMessages.insert({
        entryId: newId,
        text: entry.assistantMessage.text,
        tenantId: ctx.tenantId,
      });
    }
    if (entry.toolCall) {
      await tx.mutate.toolCalls.insert({
        entryId: newId,
        toolName: entry.toolCall.toolName,
        anthropicId: entry.toolCall.anthropicId,
        input: entry.toolCall.input,
        output: entry.toolCall.output,
        outputBlobId: entry.toolCall.outputBlobId,
        isError: entry.toolCall.isError,
        completedAt: entry.toolCall.completedAt,
        tenantId: ctx.tenantId,
      });
      // TOOL_CALL_OUTPUT_V1_BACKCOMPAT start
      if (
        entry.toolCall.completedAt !== null &&
        entry.toolCall.outputBlobId === null
      ) {
        legacyToolCallCopies.push({
          source: { entryId: entry.id, chatId: entry.chatId },
          destination: { entryId: newId, chatId: newChatId },
        });
      }
      // TOOL_CALL_OUTPUT_V1_BACKCOMPAT end
    }
    if (entry.assistantReasoning) {
      await tx.mutate.assistantReasonings.insert({
        entryId: newId,
        text: entry.assistantReasoning.text,
        signature: entry.assistantReasoning.signature,
        tenantId: ctx.tenantId,
      });
    }
    if (entry.systemMessage) {
      await tx.mutate.systemMessages.insert({
        entryId: newId,
        text: entry.systemMessage.text,
        tenantId: ctx.tenantId,
      });
    }
    if (entry.userAction) {
      await tx.mutate.userActions.insert({
        entryId: newId,
        userId: entry.userAction.userId,
        actionType: entry.userAction.actionType,
        metadata: entry.userAction.metadata,
        tenantId: ctx.tenantId,
      });
    }
  }

  const actionEntryId = generateId();
  await tx.mutate.chatEntries.insert({
    id: actionEntryId,
    chatId: newChatId,
    entryType: markerEntryType,
    createdAt: now,
    previousEntryId: previousNewId,
    tenantId: ctx.tenantId,
  });
  if (actor.userId) {
    await tx.mutate.userActions.insert({
      entryId: actionEntryId,
      userId: actor.userId,
      actionType: "pushDraftToLive",
      metadata: { draftName: sourceBranch.name },
      tenantId: ctx.tenantId,
    });
  } else {
    await tx.mutate.systemMessages.insert({
      entryId: actionEntryId,
      text: `Pushed “${sourceBranch.name}” to live`,
      tenantId: ctx.tenantId,
    });
  }

  const sourceContributors = await tx.run(
    zql.branchContributors.where("branchId", sourceBranch.id)
  );
  const sourceContributorByUserId = new Map(
    sourceContributors.map((c) => [c.userId, c])
  );
  // Contributors are the human facepile. Carry every source contributor onto
  // main, plus the pushing user; a pure service account pusher has no human to
  // add.
  const contributorIds = new Set(sourceContributorByUserId.keys());
  if (actor.userId) contributorIds.add(actor.userId);
  for (const contributorId of contributorIds) {
    const sourceContributor = sourceContributorByUserId.get(contributorId);
    await tx.mutate.branchContributors.upsert({
      branchId: mainBranchId,
      workflowId: sourceBranch.workflowId,
      userId: contributorId,
      tenantId: ctx.tenantId,
      ...(sourceContributor
        ? { lastMessagedAt: sourceContributor.lastMessagedAt }
        : {}),
    });
  }

  const [wf] = await tx.run(zql.workflows.where("id", sourceBranch.workflowId));

  // A merged draft is no longer the space branch's claim on this workflow; the
  // ref goes on showing the content by inheriting it from its own tree.
  const spaceBranchMemberships = await tx.run(
    zql.spaceBranchMembers.where("workflowId", sourceBranch.workflowId)
  );
  for (const member of spaceBranchMemberships) {
    if (member.workflowBranchId !== sourceBranch.id) continue;
    await tx.mutate.spaceBranchMembers.update({
      spaceBranchId: member.spaceBranchId,
      workflowId: member.workflowId,
      workflowBranchId: null,
    });
  }

  if (deleteSourceBranch) {
    await tx.mutate.workflowBranches.delete({ id: sourceBranch.id });
  }

  if (mainBranch) {
    await tx.mutate.workflowBranches.update({
      id: mainBranchId,
      headCommitId: newCommitId,
      lastChatEntryAt: now,
    });
  }

  await tx.mutate.workflows.update({
    id: sourceBranch.workflowId,
    updatedAt: now,
    pausedAt: null,
    ...(wf?.autofixBranchId === sourceBranch.id
      ? { autofixBranchId: null }
      : {}),
    ...(wf?.autoImproveBranchId === sourceBranch.id
      ? { autoImproveBranchId: null }
      : {}),
  });

  // TOOL_CALL_OUTPUT_V1_BACKCOMPAT start
  return legacyToolCallCopies;
  // TOOL_CALL_OUTPUT_V1_BACKCOMPAT end
}
